<template>
  <div
    v-loading="loading" class="authenticate page-container overflow-hidden flex flex-col"
  >
    <template v-if="url && !loading">
      <iframe class="flex-1" :src="url" frameborder="0" />
    </template>
    <template v-if="!url && !loading">
      <main
        v-if="!(props.form=='AuthenticationDetail')"
        class="page-content flex flex-col flex-1 overflow-hidden justify-center items-center"
      >
        <div
          class="authenticate-logo"
          :class="[
            authStatus == 0 ? 'info' : '',
            authStatus == 1 ? 'warning' : '',
            authStatus == 2 ? 'danger' : '',
          ]"
        />
        <div class="title mt-[4px] mb-[12px] text-base font-medium">
          {{ title }}
        </div>
        <div v-if="authStatus == 0" class="desc text-sm">
          尊敬的用户您好，目前只有完成企业实名后才能开放使用系统功能，点击下方去实名可进行实名操作
        </div>
        <div v-if="authStatus == 1" class="desc text-sm">
          尊敬的用户您好，目前正在实名中，请稍后查看
        </div>
        <div v-if="authStatus == 2" class="desc text-sm">
          失败原因：{{ failReason }}
        </div>
        <div class="action flex justify-center">
          <el-button
            v-if="authStatus == 0 || authStatus == 1"
            type="primary"
            class="mt-[32px]"
            @click="gotoRealNameAuthentication"
          >
            去实名
          </el-button>
          <el-button
            v-if="authStatus == 2"
            type="primary"
            class="mt-[32px]"
            @click="gotoRealNameAuthentication"
          >
            重新实名
          </el-button>
        </div>
      </main>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { useSessionStorage } from '@vueuse/core';
import {
  ESystemType,
  getSysTypeValue,
  ISignAuthDetail,
  ISignAuthInfo,
  querySignAuthDetail,
  querySignAuthUrl,
} from '@core/api';
import { computed, ref, watch } from 'vue';
import { isNull } from '@core/tools';

defineOptions({ naem: 'Authenticates' });

const props = defineProps({
  form: {
    type: String,
    default: 'Authenticate',
  },
  reapply: {
    type: Number,
    default: 0,
  },
});

const signAuth = useSessionStorage<ISignAuthInfo>(
  'signAuth',
  {} as ISignAuthInfo,
);

const forceAuth = ref(signAuth.value.forceAuth);
const authStatus = ref(signAuth.value.authStatus);
const failReason = ref(signAuth.value.failReason);
const loading = ref(true);
let authenticationData = ref<ISignAuthDetail>({} as ISignAuthDetail);

const url = ref('');

const title = computed(() => {
  let str = '';

  switch (authStatus.value) {
    case 0:
      str = '未实名';
      break;
    case 1:
      str = '实名中';
      break;
    case 2:
      str = '实名失败';
      break;
    case 3: {
      str = '已实名';
      break;
    }
    default:
      break;
  }
  return str;
});

const gotoRealNameAuthentication = async () => {
  loading.value = true;
  try {
    url.value = await querySignAuthUrl(0, forceAuth.value ? 0 : 1);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  loading.value = false;
};

const fetchAuthenticationDetail = async () => {
  loading.value = true;
  if (signAuth.value.authStatus == 3) {
    try {
      authenticationData.value = await querySignAuthDetail();
    } catch (error) {
      if ((error as Error).message) ElMessage.error((error as Error).message);
    }
  }
  loading.value = false;
};

fetchAuthenticationDetail();

const afterAuthenticate = () => {
  const ports = {
    [ESystemType.SAAS]: __SAAS_APP_PORT__,
    [ESystemType.Agent]: __AGENT_APP_PORT__,
    [ESystemType.Company]: __COMPANY_APP_PORT__,
    [ESystemType.Server]: __SERVER_APP_PORT__,
  };

  const prefixs = {
    [ESystemType.SAAS]: 'identity-saas',
    [ESystemType.Agent]: 'identity-agent',
    [ESystemType.Company]: 'identity-company',
    [ESystemType.Server]: 'identity-server',
  };

  const systemTypeStr = getSysTypeValue();
  const systemType = isNull(systemTypeStr) ? undefined : Number(systemTypeStr);

  if (systemType === undefined) return ElMessage.error('获取身份失败');

  try {
    if (__ENV_DEV__) {
      window.location.href = `${location.protocol}//${location.hostname}:${ports[systemType]}/${prefixs[systemType!]}`;
    } else {
      window.location.href = `/${prefixs[systemType!]}`;
    }
  } catch (error) {
    ElMessage.error((error as Error).message);
  }
};

watch(
  () => forceAuth.value,
  async () => {
    if (forceAuth.value === 0) {
      loading.value = true;
      if (authStatus.value == 0) {
        try {
          url.value = await querySignAuthUrl(0, 1);
        } catch (error) {
          if ((error as Error).message) ElMessage.error((error as Error).message);
        }
      }
      loading.value = false;
    }
  },
  { immediate: true },
);

watch(
  () => authStatus.value,
  async () => {
    if (authStatus.value == 3 && props.form == 'EnterpriseInfo') {
      loading.value = true;
      if (props.reapply == 1) {
        try {
          url.value = await querySignAuthUrl(2, 1);
        } catch (error) {
          if ((error as Error).message) ElMessage.error((error as Error).message);
        }
      } else {
        try {
          url.value = await querySignAuthUrl(1, 1);
        } catch (error) {
          if ((error as Error).message) ElMessage.error((error as Error).message);
        }
      }
      loading.value = false;
    } else if (authStatus.value == 3 && props.form == 'Authenticate') {
      loading.value = true;
      afterAuthenticate();
      loading.value = false;
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.authenticate {
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #fff;
}
.authenticate-logo {
  width: 460px;
  height: 252px;
  background-size: 460px 252px;
}
.authenticate-logo.info {
  background-image: url(@core/main/assets/images/authenticate-info.png);
}
.authenticate-logo.warning {
  background-image: url(@core/main/assets/images/authenticate-warning.png);
}
.authenticate-logo.danger {
  background-image: url(@core/main/assets/images/authenticate-danger.png);
}
.title {
  color: #262626;
}
.desc {
  color: #595959;
}
</style>
